// Phase Setup Chatops — compose resolve+persist Mattermost thành 1 flow fail-soft.
// Ported from avatar-po-cli phase-04 (identical flow). Gọi sau AI setup / GitNexus setup
// trong `scaffoldThinWorkspace` (workspace-scaffold-and-finalize-orchestrator.ts) — cùng
// pattern fail-soft đã dùng cho runAiSetupPhase/runGitnexusSetupPhase ở avatar CLI.
// Try/catch toàn bộ ở CALLER (orchestrator) — hàm này NÉM lỗi để caller catch + log warn,
// KHÔNG throw process.exit — workspace vẫn dùng được, user setup lại qua `avatar chatops-connect`.
//
// UX v2: hỏi Yes/No trước khi vào flow (không ép setup) → menu chọn kênh giao tiếp
// (hiện chỉ có Mattermost, chừa chỗ mở rộng) → nếu bot mặc định "avatar" đã có token
// lưu sẵn trong Keychain thì dùng luôn; miss thì thử lấy tự động từ server NAL
// (get-bot-token, cần đã `avatar login`) trước khi hỏi tay.
//
// UX v3: validate fail (channel không tìm thấy / token bị từ chối) → retry-loop
// menu thay vì ném lỗi ngay: "Điền lại tên channel"/"Nhập lại token" (re-prompt
// field liên quan rồi validate lại) hoặc "Setup lại sau" (defer, return
// {ok:false, reason:"deferred"} — KHÔNG throw, orchestrator không coi là fail).

import {
  DEFAULT_BOT_NAME,
  DEFAULT_MM_URL,
  type MattermostCredentials,
  persistMattermostCredentials,
  validateBotTokenAndResolveChannel,
} from "../commands/comm-connect.js";
import {
  type CommSetupFlags,
  pickTeamByFlag,
  requireCommProvider,
  requireCommSetupDecision,
  resolveCommFlagsOrThrow,
} from "./comm-setup-non-interactive-flags.js";
import { fetchBotTokenFromServer } from "./fetch-mattermost-bot-token-from-server.js";
import { getBotToken, saveBotToken } from "./mattermost-token-keychain-store.js";
import { isNonInteractive } from "./non-interactive-context.js";
import { confirmOrFlag, inputOrFlag, passwordOrFlag, selectOrFlag } from "./prompt-or-flag.js";
import type { AmbiguousChannelCandidate } from "./resolve-mattermost-bot-channel-via-api-chain.js";
import { MattermostSetupError } from "./resolve-mattermost-bot-channel-via-api-chain.js";
import { log } from "./terminal-logger.js";

export interface CommSetupPhaseArgs {
  workspacePath: string;
  /** Cờ comm của `avatar init` (T2). Chỉ có tác dụng ở chế độ non-interactive. */
  flags?: CommSetupFlags;
}

// Lệnh hiển thị trong `MissingFlagError`: phase này CHỈ chạy từ `avatar init`
// (`workspace-scaffold-and-finalize-orchestrator.ts` là người gọi duy nhất), nên
// người đọc lỗi cần thấy `init`, không phải tên file nội bộ.
const COMMAND = "init";
const SETUP_FLAG_SPEC = {
  flag: "--comm-setup",
  command: COMMAND,
  hint: "--comm-setup yes|no (hoặc --comm-skip để bỏ trọn phase)",
};
const PROVIDER_FLAG_SPEC = {
  flag: "--comm-provider",
  command: COMMAND,
  hint: "--comm-provider mattermost",
};
const URL_FLAG_SPEC = {
  flag: "--mattermost-url",
  command: COMMAND,
  hint: "--mattermost-url https://mm.nal.vn",
};
const BOT_NAME_FLAG_SPEC = {
  flag: "--comm-bot-name",
  command: COMMAND,
  hint: "--comm-bot-name avatar",
};
const CHANNEL_FLAG_SPEC = {
  flag: "--comm-channel",
  command: COMMAND,
  hint: "--comm-channel team/channel",
};
const TEAM_FLAG_SPEC = { flag: "--comm-team", command: COMMAND, hint: "--comm-team <team-slug>" };
const TOKEN_FLAG_SPEC = {
  flag: "--comm-bot-token-stdin",
  command: COMMAND,
  hint: 'echo "$TOKEN" | avatar init … --non-interactive --comm-bot-token-stdin',
};
const VALIDATE_FAIL_FLAG_SPEC = {
  flag: "--on-comm-validate-fail",
  command: COMMAND,
  hint: "--on-comm-validate-fail abort|defer",
};

export type CommSetupResult = { ok: true; channelName: string } | { ok: false; reason: string };

// Prompt gọn (Yes/No → chọn kênh → URL/bot/channel/token) → resolve + validate (lõi
// comm-connect) → persist. Ném lỗi để orchestrator catch fail-soft (trừ user skip).
export async function runCommSetupPhase(args: CommSetupPhaseArgs): Promise<CommSetupResult> {
  const flags = args.flags ?? {};

  // Non-interactive: quyết định chạy/không (L4 — `--comm-setup`, cờ MỚI tới được
  // đúng site này, khác `--comm-skip` vốn bị orchestrator chặn từ trước), rồi kiểm
  // đủ cờ + đọc token TRƯỚC mọi lời gọi mạng.
  const ni = isNonInteractive();
  if (ni && !requireCommSetupDecision(flags, COMMAND)) {
    return { ok: false, reason: "skipped" };
  }
  const preset = ni ? await resolveCommFlagsOrThrow(flags, COMMAND) : undefined;

  const wantsSetup = await confirmOrFlag(ni ? true : undefined, SETUP_FLAG_SPEC, {
    message: "Bạn có muốn setup giao tiếp giữa DEV và PO không?",
    default: false,
  });
  if (!wantsSetup) {
    return { ok: false, reason: "skipped" };
  }

  const channel = await selectOrFlag(
    ni ? requireCommProvider(flags, COMMAND) : undefined,
    PROVIDER_FLAG_SPEC,
    {
      message: "Chọn kênh giao tiếp:",
      choices: [
        { name: "Mattermost (https://mm.nal.vn)", value: "mattermost" },
        { name: "Hủy", value: "cancel" },
      ],
    },
  );
  if (channel === "cancel") {
    return { ok: false, reason: "skipped" };
  }

  log.info("Setup Mattermost cho workspace...");

  const mattermostUrl = (
    await inputOrFlag(ni ? (flags.mattermostUrl ?? DEFAULT_MM_URL) : undefined, URL_FLAG_SPEC, {
      message: "Mattermost URL:",
      default: DEFAULT_MM_URL,
    })
  ).trim();
  const botName = (
    await inputOrFlag(preset?.botName, BOT_NAME_FLAG_SPEC, {
      message: "Tên bot Mattermost:",
      default: DEFAULT_BOT_NAME,
    })
  ).trim();
  let channelName = (
    await inputOrFlag(preset?.channelName, CHANNEL_FLAG_SPEC, {
      message: "Tên channel chat (hoặc team/channel):",
      validate: (v) => (v.trim().length > 0 ? true : "Không được rỗng"),
    })
  ).trim();

  // Thứ tự ưu tiên cho bot mặc định "avatar": (1) Keychain — nhanh, offline; (2)
  // miss → server NAL get-bot-token — cần đã `avatar login`; (3) cả 2 fail → hỏi
  // tay. Bot khác "avatar" giữ nguyên: Keychain → hỏi tay (server chỉ có token avatar).
  // Non-interactive: token CHỈ từ stdin — không rơi ngầm về keychain/server.
  const savedToken =
    preset === undefined && botName === DEFAULT_BOT_NAME ? await getBotToken(botName) : null;
  const serverToken =
    preset === undefined && !savedToken && botName === DEFAULT_BOT_NAME
      ? await fetchBotTokenFromServer(botName)
      : null;
  let token: string;
  if (preset) {
    token = preset.botToken;
  } else if (savedToken) {
    log.info("Dùng bot token @avatar đã lưu trong keychain.");
    token = savedToken;
  } else if (serverToken) {
    log.info("Đã lấy bot token @avatar từ server NAL.");
    token = serverToken;
    await saveBotToken(botName, token);
  } else {
    if (botName === DEFAULT_BOT_NAME) {
      log.dim("Chưa login? Chạy avatar login, hoặc dán token thủ công:");
    }
    token = (
      await passwordOrFlag(undefined, TOKEN_FLAG_SPEC, {
        message: "Dán bot token Mattermost:",
        mask: "*",
      })
    ).trim();
    if (!token) {
      throw new Error("Token rỗng — hủy Setup Chatops.");
    }
  }

  // Retry-loop: validate fail → menu sửa (điền lại channel / nhập lại token) hoặc
  // defer setup sau (`avatar chatops-connect`). GIỮ url/bot đã nhập — chỉ re-prompt
  // field liên quan tới loại lỗi. Pass (break) → tiếp tục persist bên dưới.
  let resolved: Awaited<ReturnType<typeof validateBotTokenAndResolveChannel>>;
  for (;;) {
    try {
      resolved = await validateBotTokenAndResolveChannel(
        mattermostUrl,
        token,
        botName,
        channelName,
        async (candidates: AmbiguousChannelCandidate[]) =>
          preset
            ? pickTeamByFlag(candidates, flags.commTeam, COMMAND)
            : selectOrFlag(undefined, TEAM_FLAG_SPEC, {
                message: `Channel có ở ${candidates.length} team — chọn:`,
                choices: candidates.map((c) => ({
                  name: `${c.teamName}/${channelName} (${c.teamDisplayName})`,
                  value: c,
                })),
              }),
      );
      break;
    } catch (err) {
      const kind = err instanceof MattermostSetupError ? err.kind : "other";
      const message = err instanceof Error ? err.message : String(err);
      log.warn(message);

      const isTokenError = kind === "token";

      // Non-interactive: `--on-comm-validate-fail` thay menu. `abort` (mặc định)
      // ném lại lỗi gốc ⇒ orchestrator fail-soft ghi warn, KHÔNG ghi credential nào.
      if (preset) {
        if (preset.onValidateFail === "defer") return { ok: false, reason: "deferred" };
        throw err;
      }

      const fixChoice = await selectOrFlag(undefined, VALIDATE_FAIL_FLAG_SPEC, {
        message: "Xử lý thế nào?",
        choices: [
          {
            name: isTokenError ? "Nhập lại token" : "Điền lại tên channel",
            value: "retry",
          },
          { name: "Setup lại sau với lệnh avatar chatops-connect", value: "defer" },
        ],
      });

      if (fixChoice === "defer") {
        return { ok: false, reason: "deferred" };
      }

      // retry: re-prompt field tương ứng loại lỗi, giữ nguyên các field khác.
      if (isTokenError) {
        token = (
          await passwordOrFlag(undefined, TOKEN_FLAG_SPEC, {
            message: "Dán bot token Mattermost:",
            mask: "*",
          })
        ).trim();
        if (!token) {
          throw new Error("Token rỗng — hủy Setup Chatops.");
        }
      } else {
        channelName = (
          await inputOrFlag(undefined, CHANNEL_FLAG_SPEC, {
            message: "Tên channel chat (hoặc team/channel):",
            validate: (v) => (v.trim().length > 0 ? true : "Không được rỗng"),
          })
        ).trim();
      }
      // loop lại để validate với field đã re-prompt.
    }
  }

  // Validate thành công → lưu lại token cho lần sau (kể cả bot khác avatar).
  await saveBotToken(botName, token);

  const creds: MattermostCredentials = {
    mattermostUrl,
    botName,
    botToken: token,
    teamId: resolved.teamId,
    defaultChannelId: resolved.channelId,
    defaultChannelName: resolved.channelName,
  };
  await persistMattermostCredentials(args.workspacePath, creds);
  log.success(`Mattermost ready · channel: ${resolved.channelName}`);
  return { ok: true, channelName: resolved.channelName };
}
